import { useEffect, useState } from "react";

const THEMES = [
  {
    id: "",
    label: "通用",
    headerColor: "#f5f5f5",
    headerBorder: "#e0e0e0",
    primaryColor: "#1a1a1a",
    textColor: "#1a1a1a",
  },
  {
    id: "trade",
    label: "外贸",
    headerColor: "#0d1f3d",
    headerBorder: "#0d1f3d",
    primaryColor: "#2563eb",
    textColor: "#fff",
  },
  {
    id: "business",
    label: "商务",
    headerColor: "#0e0d28",
    headerBorder: "#0e0d28",
    primaryColor: "#6d5fd5",
    textColor: "#fff",
  },
  {
    id: "finance",
    label: "金融",
    headerColor: "#100f0b",
    headerBorder: "#100f0b",
    primaryColor: "#c9a240",
    textColor: "#ece8d4",
  },
  {
    id: "eco",
    label: "环保",
    headerColor: "#0a1c0d",
    headerBorder: "#0a1c0d",
    primaryColor: "#22863a",
    textColor: "#fff",
  },
  {
    id: "commerce",
    label: "电商",
    headerColor: "#1c0d06",
    headerBorder: "#1c0d06",
    primaryColor: "#ea6020",
    textColor: "#fff",
  },
  {
    id: "minimal",
    label: "简约",
    headerColor: "#f5f5f5",
    headerBorder: "#e5e5e5",
    primaryColor: "#404040",
    textColor: "#383838",
  },
  {
    id: "tech",
    label: "科技",
    headerColor: "#060d1f",
    headerBorder: "#060d1f",
    primaryColor: "#06c8c8",
    textColor: "#a8f0ec",
  },
  {
    id: "neutral",
    label: "中性",
    headerColor: "#dcd7cc",
    headerBorder: "#c8c3b8",
    primaryColor: "#4a4540",
    textColor: "#2a2620",
  },
] as const;

const RADII = [
  { id: "", label: "默认", preview: "rounded-[0.625rem]" },
  { id: "sharp", label: "直角", preview: "rounded-none" },
  { id: "rounded", label: "圆角", preview: "rounded-2xl" },
] as const;

const LS_THEME = "crm-theme";
const LS_RADIUS = "crm-radius";

export const ThemeSwitcher = () => {
  const [activeTheme, setActiveTheme] = useState(
    () => localStorage.getItem(LS_THEME) ?? "",
  );
  const [activeRadius, setActiveRadius] = useState(
    () => localStorage.getItem(LS_RADIUS) ?? "",
  );

  useEffect(() => {
    const el = document.documentElement;
    if (activeTheme) {
      el.dataset.theme = activeTheme;
    } else {
      delete el.dataset.theme;
    }
    localStorage.setItem(LS_THEME, activeTheme);
  }, [activeTheme]);

  useEffect(() => {
    const el = document.documentElement;
    if (activeRadius) {
      el.dataset.radius = activeRadius;
    } else {
      delete el.dataset.radius;
    }
    localStorage.setItem(LS_RADIUS, activeRadius);
  }, [activeRadius]);

  return (
    <div className="space-y-6">
      {/* Color themes */}
      <div>
        <p className="text-sm font-medium mb-3">配色方案</p>
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
          {THEMES.map((theme) => {
            const isActive = activeTheme === theme.id;
            return (
              <button
                key={theme.id || "general"}
                type="button"
                onClick={() => setActiveTheme(theme.id)}
                title={theme.label}
                className={`group relative flex flex-col items-center gap-1.5 rounded-lg border-2 p-2 transition-all ${
                  isActive
                    ? "border-primary shadow-sm"
                    : "border-transparent hover:border-border"
                }`}
              >
                {/* Mini header preview */}
                <div
                  className="w-full h-8 rounded overflow-hidden flex items-center px-2 gap-1.5 shrink-0"
                  style={{
                    backgroundColor: theme.headerColor,
                    border: `1px solid ${theme.headerBorder}`,
                  }}
                >
                  {/* Logo placeholder */}
                  <div
                    className="w-3 h-3 rounded-sm shrink-0"
                    style={{ backgroundColor: theme.primaryColor }}
                  />
                  {/* Nav dots */}
                  <div className="flex gap-1 flex-1">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-1.5 rounded-full"
                        style={{
                          width: i === 1 ? "16px" : "10px",
                          backgroundColor:
                            i === 1 ? theme.primaryColor : theme.textColor,
                          opacity: i === 1 ? 1 : 0.5,
                        }}
                      />
                    ))}
                  </div>
                </div>
                {/* Button sample */}
                <div
                  className="w-full h-4 rounded text-[8px] flex items-center justify-center font-medium"
                  style={{
                    backgroundColor: theme.primaryColor,
                    color: theme.id === "finance" ? "#1a1400" : "#fff",
                  }}
                >
                  Btn
                </div>
                <span className="text-xs text-muted-foreground">{theme.label}</span>
                {isActive && (
                  <div className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-primary" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Border radius */}
      <div>
        <p className="text-sm font-medium mb-3">圆角风格</p>
        <div className="flex gap-3">
          {RADII.map((r) => {
            const isActive = activeRadius === r.id;
            return (
              <button
                key={r.id || "default"}
                type="button"
                onClick={() => setActiveRadius(r.id)}
                className={`flex flex-col items-center gap-2 px-4 py-3 border-2 rounded-lg transition-all ${
                  isActive
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/40"
                }`}
              >
                {/* Radius preview box */}
                <div
                  className={`w-10 h-7 border-2 border-current ${r.preview}`}
                  style={{ opacity: 0.7 }}
                />
                <span className="text-xs text-muted-foreground">{r.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
